import React, {useEffect, useMemo, useState} from 'react';
import {IconButton, Typography} from '@mui/material';
import {t} from 'i18next';
import _ from 'lodash';
import {ReactComponent as InfoIcon} from '../../../../assests/svg/info_icon.svg';
import {AurSwitch} from '../../../common/aur-switch/AurSwitch';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import ReportConfigurationInfoDialog from '../modal/ReportConfigurationInfoDialog';
import {EMAIL_ADDRESS_REGEX} from '../../../../constants/constants';
import RelationalGroupFieldsSelectionDialog, {
  RelationalGroupFieldType,
} from '../modal/RelationalGroupFieldsSelectionDialog';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {getRelationalGroups} from '../../../../api/EcaServices';
import {setRelationalGroupFields} from '../../../../redux/slices/strSlice';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';

const stringPath = 'eca.searchTermReport.resultSetting.';
type ResultSettingKeyType =
  | 'includeUniqueHitsFlag'
  | 'storeHitQueryStringsFlag'
  | 'relationalGroupFieldList';

export type ResultSettingsType = {
  includeUniqueHitsFlag: boolean;
  storeHitQueryStringsFlag: boolean;
  relationalGroupFieldList: string[];
  emailList: string[];
  isValid: boolean;
};

interface Props {
  initialData: ResultSettingsType | null;
  onDataChange: (data: ResultSettingsType) => void;
}

const ResultSetting: React.FC<Props> = ({initialData, onDataChange}) => {
  const dispatch = useDispatch();
  const [settingSwitchValues, setSettingSwitchValues] = useState<{
    [key in ResultSettingKeyType]: boolean;
  }>({
    includeUniqueHitsFlag: false,
    storeHitQueryStringsFlag: false,
    relationalGroupFieldList: false,
  });

  const [configInfoModalVisible, setConfigInfoModalVisible] =
    useState<boolean>(false);

  const [relationalGroupModalVisible, setRelationalGroupModalVisible] =
    useState<boolean>(false);

  const [selectedRelationalFieldIds, setSelectedRelationalFieldIds] = useState<
    string[]
  >([]);

  const [emailInputError, setEmailInputError] = useState<string>('');
  const [emailInput, setEmailInput] = useState<string>('');
  const [requestLoading, setRequestLoading] = useState<boolean>(false);
  const {trackGAEvent} = useAnalytics();

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const {projectId} = useSelector((state: RootState) => state.reports);
  const {relationalGroupFields} = useSelector((state: RootState) => state.STR);

  useEffect(() => {
    if (initialData) {
      const {
        includeUniqueHitsFlag,
        storeHitQueryStringsFlag,
        relationalGroupFieldList,
        emailList,
      } = initialData;
      setSettingSwitchValues({
        includeUniqueHitsFlag: includeUniqueHitsFlag,
        storeHitQueryStringsFlag: storeHitQueryStringsFlag,
        relationalGroupFieldList: relationalGroupFieldList.length !== 0,
      });
      setSelectedRelationalFieldIds(relationalGroupFieldList);
      if (emailList.length !== 0) {
        setEmailInput(emailList.join(' '));
        setEmailInputError('');
      }
    }
    fetchRelationalGroups((relationalGroupFields ?? []).length === 0);
  }, []);

  useEffect(() => {
    if (onDataChange) {
      let emailList: string[] = [];
      if (emailInputError === '')
        emailList = emailInput
          .split(' ')
          .filter((item) => item !== '')
          .map((item) => item.trim());
      onDataChange({
        includeUniqueHitsFlag: settingSwitchValues.includeUniqueHitsFlag,
        storeHitQueryStringsFlag: settingSwitchValues.storeHitQueryStringsFlag,
        relationalGroupFieldList: settingSwitchValues.relationalGroupFieldList
          ? selectedRelationalFieldIds
          : [],
        emailList: emailList,
        isValid: isValid,
      });
    }
  }, [
    emailInput,
    selectedRelationalFieldIds,
    settingSwitchValues,
    onDataChange,
    emailInputError,
  ]);

  const selectedRelationalFieldNames = useMemo(() => {
    return (relationalGroupFields ?? [])
      .filter((item) => selectedRelationalFieldIds.includes(item.id.toString()))
      .map((item) => item.name);
  }, [selectedRelationalFieldIds, relationalGroupFields]);

  const fetchRelationalGroups = async (showLoader = true) => {
    setRequestLoading(showLoader);
    try {
      const path = `?clientId=${clientId}&projectId=${projectId}`;
      const response = await getRelationalGroups(path);
      const data = _.get(
        response,
        'data.data',
        []
      ) as RelationalGroupFieldType[];
      dispatch(setRelationalGroupFields(data));
    } finally {
      setRequestLoading(false);
    }
  };

  const toogleInfoModal = () => {
    setConfigInfoModalVisible((prev) => !prev);
  };

  const validateEmail = (val: string, isBlur = false) => {
    const emails = val
      .split(' ')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    return isBlur
      ? emails.some((email) => !EMAIL_ADDRESS_REGEX.test(email))
      : emails.some(
          (email) => email.length >= 3 && !EMAIL_ADDRESS_REGEX.test(email)
        );
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmailInput(value);

    const invalidEmails = validateEmail(value, false);
    if (invalidEmails) {
      setEmailInputError(t(stringPath + 'emailInvalid'));
    } else {
      setEmailInputError('');
    }
  };

  const handleBlur = () => {
    const invalidEmails = validateEmail(emailInput, true);
    if (invalidEmails) {
      setEmailInputError(t(stringPath + 'emailInvalid'));
    } else {
      setEmailInputError('');
    }
  };

  const handleRelationGroupModalClose = () => {
    setSettingSwitchValues((prev) => ({
      ...prev,
      relationalGroupFieldList: false,
    }));
    setRelationalGroupModalVisible(false);
    setSelectedRelationalFieldIds([]);
  };

  const handleRelationGroupSave = (value: string[]) => {
    setRelationalGroupModalVisible(false);
    setSelectedRelationalFieldIds(value);
  };

  const handleSwitchClickEvent = (key: ResultSettingKeyType, name: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: name,
        type: GA_CLICK_TYPES.TOGGLE,
        region: GA_REGIONS.TAB,
        action: settingSwitchValues[key]
          ? t('gaTracking.str.toggleActionOff')
          : t('gaTracking.str.toggleActionOn'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.steps.resultSettings'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
    if (
      key === 'relationalGroupFieldList' &&
      settingSwitchValues.relationalGroupFieldList === false
    ) {
      setRelationalGroupModalVisible(true);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t('gaTracking.str.relationalGroupModal'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    } else if (
      key === 'relationalGroupFieldList' &&
      settingSwitchValues.relationalGroupFieldList
    ) {
      setSelectedRelationalFieldIds([]);
    }
    setSettingSwitchValues((prev) => {
      return {
        ...prev,
        [key]: !prev[key],
      };
    });
  };

  const renderSwitchItem = (title: string, key: ResultSettingKeyType) => {
    return (
      <div className="switch-title-row">
        <Typography className="typo-text body3_size caption-label" tabIndex={-1}>
          {t(stringPath + title)}
        </Typography>
        <AurSwitch
          checked={settingSwitchValues[key] ?? false}
          onChange={handleSwitchClickEvent.bind(
            this,
            key,
            t(stringPath + title)
          )}
          ariaLabel={t(stringPath + title)}
          dataTestId={key}
        />
      </div>
    );
  };

  const isValid = !validateEmail(emailInput, true);

  return (
    <div className="result-setting-container">
      <Typography className="typo-text headline3_size" tabIndex={-1}>
        {t(stringPath + 'title')}
      </Typography>

      <div className="form-container">
        {/** Left Side Content */}
        <div className="left-panel">
          <div className="title-row">
            <Typography className="typo-text subhead4_size">
              {t(stringPath + 'reportConfig')}
            </Typography>
            <IconButton
              onClick={toogleInfoModal}
              data-testid="report-config-info-button"
              aria-label={t('eca.searchTermReport.resultSetting.reportConfig')}
            >
              <InfoIcon width={24} height={24} />
            </IconButton>
          </div>

          <div className="switch-parent-div">
            {renderSwitchItem('includeUniqueHit', 'includeUniqueHitsFlag')}
            {renderSwitchItem('storeHitQuery', 'storeHitQueryStringsFlag')}
            {renderSwitchItem(
              settingSwitchValues.relationalGroupFieldList
                ? 'identifyRelationalGroup'
                : 'includeRelationalGroup',
              'relationalGroupFieldList'
            )}
          </div>
          {selectedRelationalFieldNames.length > 0 &&
            settingSwitchValues.relationalGroupFieldList && (
              <div className="relational-group-list">
                <ul>
                  {selectedRelationalFieldNames.map((item, index) => (
                    <li key={`relational-item-${index + 1}`}>
                      <Typography className="typo-text label4_size">
                        {item}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </div>
        {/** Right Side Content */}
        <div className="right-panel">
          <Typography className="typo-text subhead4_size">
            {t(
              stringPath +
                (settingSwitchValues.relationalGroupFieldList
                  ? 'addCollaboratorsToNotify'
                  : 'emailNotifyList')
            )}
          </Typography>
          <AurTextField
            className={`aur-textfield custom-text-field email-field ${emailInputError ? 'Mui-error' : ''} ${settingSwitchValues.relationalGroupFieldList ? 'Relation-Group-Enabled' : ''}`}
            variant="filled"
            multiline
            minRows={3}
            maxRows={5}
            placeholder={t(
              stringPath +
                (settingSwitchValues.relationalGroupFieldList
                  ? 'addCollaboratorPlaceholder'
                  : 'emailNotifyPlaceholder')
            )}
            value={emailInput}
            onChange={handleEmailChange}
            error={Boolean(emailInputError)}
            helperText={emailInputError}
            data-testid="email-input-field"
            onBlur={handleBlur}
            ariaLabel={t(
              stringPath +
                (settingSwitchValues.relationalGroupFieldList
                  ? 'addCollaboratorsToNotify'
                  : 'emailNotifyList')
            )}
          />
        </div>
      </div>
      <ReportConfigurationInfoDialog
        isOpen={configInfoModalVisible}
        onClose={toogleInfoModal}
      />
      <RelationalGroupFieldsSelectionDialog
        isOpen={relationalGroupModalVisible}
        onSave={handleRelationGroupSave}
        onClose={handleRelationGroupModalClose}
        selectedFields={selectedRelationalFieldIds}
      />
      <AurLoadingIndicator overlay={true} isOpen={requestLoading} />
    </div>
  );
};

export default ResultSetting;
